import { QueryParamsFilter } from "@finway-group/shared/lib/models"
import { useCallback, useEffect, useState } from "react"
import { useLocalStorage } from "~Shared/hooks/useLocalStorage"
import { adjustTimeRangeOfDateFields, convertFilterObjectIntoQueryFormat } from "~Shared/utils/filter.utils"

/**
 * @param localStorageKey - The key to use for local storage. If not provided, the filters will be stored in the state only.
 */
export const useFilter = <FilterType extends Record<string, any>>({ localStorageKey }: { localStorageKey?: string }) => {
    const [stateFilters, setStateFilters] = useState<FilterType>({} as FilterType)
    const [storageFilters, setStorageFilters] = useLocalStorage<FilterType>(localStorageKey ?? "", {} as FilterType)
    const useStorage = Boolean(localStorageKey)
    const filters = useStorage ? storageFilters : stateFilters
    const setFilters = useStorage ? setStorageFilters : setStateFilters

    const [filterQuery, setFilterQuery] = useState<QueryParamsFilter>({})
    const [isFilterModalShowing, setIsFilterModalShowing] = useState<boolean>(false)

    const showFilterModal = () => {
        setIsFilterModalShowing(true)
    }
    const hideFilterModal = () => {
        setIsFilterModalShowing(false)
    }

    useEffect(() => {
        if (localStorageKey) {
            localStorage.setItem(localStorageKey, JSON.stringify(filters))
        }
        const newFilterQuery = convertFilterObjectIntoQueryFormat(filters, true)
        setFilterQuery(newFilterQuery)
    }, [filters, localStorageKey])

    const onFilter = useCallback(
        (newFilters: FilterType) => {
            const sanitized = { ...newFilters }

            adjustTimeRangeOfDateFields(sanitized)

            Object.keys(sanitized).forEach((key) => {
                if (sanitized[key] === undefined || sanitized[key] === null || sanitized[key] === "") {
                    delete sanitized[key]
                }
            })

            setFilters(sanitized)
            hideFilterModal()
        },
        [setFilters],
    )

    const onRemoveFilter = useCallback(
        (filterKey: keyof FilterType) => {
            if (!filterKey) {
                setFilters({} as FilterType)
                return
            }
            const newFilters = { ...filters }
            delete newFilters[filterKey]

            setFilters(newFilters)
        },
        [filters, setFilters],
    )

    return {
        filters,
        filterQuery,
        onFilter,
        onRemoveFilter,
        isFilterModalShowing,
        showFilterModal,
        hideFilterModal,
    }
}
